import { Link } from "react-router-dom";
import { Sparkles, Printer, Star, ArrowRight, Check } from "lucide-react";

/**
 * The Plate Buddies offer ladder — brand-sheet verbatim.
 *
 *   Rung 1 — FREE Buddy  →  /create (no account needed)
 *   Rung 2 — $5 printable pack (simple_digital_pack)
 *   Rung 3 — $14.99 keepsake Buddy (buddy_print — mat + magnet + recipe)
 *
 * Rendered at the top of /shop above the kits grid. Kits stay standalone
 * below so parents can still "unbox not build" if they prefer.
 *
 * Connector line reinforces the one-story reading of the three rungs:
 *   "Start free. Print the adventure for $5. Keep it forever for $14.99."
 */
export default function OfferLadder() {
  return (
    <section
      data-testid="section-offer-ladder"
      className="relative overflow-hidden bg-white border-b border-pb-brand-purple/10"
    >
      {/* Decorative purple halo on the left, teal on the right — pulled
          from the brand-guide palette rules (Purple leads, Teal supports). */}
      <div
        aria-hidden
        className="absolute -top-24 -left-16 w-[420px] h-[420px] opacity-[0.08] pointer-events-none"
        style={{ background: "radial-gradient(circle, #4E2C80 0%, transparent 65%)", filter: "blur(28px)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-24 -right-16 w-[420px] h-[420px] opacity-[0.08] pointer-events-none"
        style={{ background: "radial-gradient(circle, #06A5A1 0%, transparent 65%)", filter: "blur(28px)" }}
      />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-display font-black uppercase tracking-widest text-pb-brand-teal inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Three ways to start
          </p>
          <h2 className="mt-3 font-brand-display font-black text-4xl sm:text-5xl text-pb-brand-ink leading-[1.05] tracking-tight">
            Start free.<br />Print the adventure.<br />Keep it forever.
          </h2>
          <p className="mt-4 font-brand-body text-pb-brand-ink/65 text-base sm:text-lg">
            Three ways to meet your Buddy — start wherever feels right.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {/* Rung 1 — Free */}
          <Rung
            testid="ladder-rung-free"
            tier="Free"
            tierColor="bg-pb-brand-purple/10 text-pb-brand-purple"
            icon={Sparkles}
            iconBg="bg-pb-brand-purple"
            title="Free Buddy"
            body="Design a Buddy together and see it come to life. No account needed to peek."
            perks={[
              "AI character in ~30 seconds",
              "Full-color Buddy on screen",
              "Zero commitment, zero spam",
            ]}
            cta="Create a Buddy"
            to="/create"
          />

          {/* Rung 2 — $5 printable pack (maps to simple_digital_pack on checkout) */}
          <Rung
            testid="ladder-rung-printable"
            tier="$5"
            tierColor="bg-pb-brand-teal/15 text-pb-brand-teal"
            icon={Printer}
            iconBg="bg-pb-brand-teal"
            title="The $5 printable pack"
            body="Your child's Buddy as an under-the-plate printable, plus a coloring sheet of their very own character. Print at home, play tonight."
            perks={[
              "Full-color Buddy card (PDF)",
              "Matching coloring page (PDF)",
              "Delivered to your inbox instantly",
            ]}
            cta="Make the printable pack"
            to="/create"
            featured
          />

          {/* Rung 3 — $14.99 keepsake Buddy (maps to buddy_print at checkout) */}
          <Rung
            testid="ladder-rung-keepsake"
            tier="$14.99"
            tierColor="bg-pb-brand-orange/15 text-pb-brand-orange"
            icon={Star}
            iconBg="bg-pb-brand-orange"
            title="The keepsake Buddy"
            body="Their character, laminated to last, with a custom magnet — ready for the fridge, the lunchbox note, or years of dinners."
            perks={[
              "Laminated 8\" Plate Buddy mat",
              "2.5\" fridge magnet of their Buddy",
              "Coordinated recipe card",
            ]}
            cta="Keep it forever"
            to="/create"
          />
        </div>

        <p
          data-testid="ladder-connector"
          className="mt-10 text-center font-brand-body text-sm sm:text-base italic text-pb-brand-ink/60 max-w-2xl mx-auto"
        >
          Start free. Print the adventure for $5. Keep it forever for $14.99.
        </p>
      </div>
    </section>
  );
}

function Rung({ testid, tier, tierColor, icon: Icon, iconBg, title, body, perks, cta, to, featured }) {
  return (
    <div
      data-testid={testid}
      className={
        featured
          ? "relative rounded-[2rem] bg-white p-7 border-[3px] border-pb-brand-teal/60 shadow-[0_24px_60px_rgba(6,165,161,0.18)] hover:-translate-y-1 transition-all duration-300"
          : "relative rounded-[2rem] bg-white p-7 border border-pb-brand-ink/10 shadow-[0_18px_40px_rgba(28,22,40,0.06)] hover:-translate-y-1 hover:shadow-[0_24px_52px_rgba(28,22,40,0.1)] transition-all duration-300"
      }
    >
      {featured && (
        <span className="absolute -top-3 left-6 bg-pb-brand-teal text-white text-[10px] font-brand-display font-black uppercase tracking-widest rounded-full px-3 py-1 shadow-[0_6px_14px_rgba(6,165,161,0.35)]">
          Most families start here
        </span>
      )}
      <div className="flex items-center gap-3">
        <span className={`inline-flex h-12 w-12 rounded-2xl items-center justify-center text-white shadow-sm ${iconBg}`}>
          <Icon className="h-5 w-5" strokeWidth={2.5} />
        </span>
        <span className={`text-[11px] font-brand-display font-black uppercase tracking-widest rounded-full px-2.5 py-1 ${tierColor}`}>
          {tier}
        </span>
      </div>
      <h3 className="mt-5 font-brand-display font-black text-2xl text-pb-brand-ink leading-tight">
        {title}
      </h3>
      <p className="mt-2 font-brand-body text-sm text-pb-brand-ink/65 leading-relaxed">
        {body}
      </p>
      <ul className="mt-5 space-y-2">
        {perks.map((p) => (
          <li key={p} className="flex items-start gap-2 font-brand-body text-sm text-pb-brand-ink/80">
            <Check className="h-4 w-4 text-pb-brand-teal mt-0.5 flex-shrink-0" strokeWidth={3} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <Link
        to={to}
        data-testid={`${testid}-cta`}
        className={
          featured
            ? "mt-6 group inline-flex items-center justify-center gap-2 w-full rounded-full bg-pb-brand-teal text-white font-brand-display font-black px-5 py-3 shadow-[0_10px_24px_rgba(6,165,161,0.35)] hover:scale-[1.02] transition"
            : "mt-6 group inline-flex items-center justify-center gap-2 w-full rounded-full bg-pb-brand-ink text-white font-brand-display font-black px-5 py-3 hover:bg-pb-brand-purple transition"
        }
      >
        {cta}
        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    </div>
  );
}
